import { ROUTES } from "../../common/constant/routes";
import { OverviewCounts } from "../../common/requests/overview/endpoints";
import { formatCount, formatMoney } from "../../common/utils/format";

export type InboxKind = "orders" | "payouts" | "applications" | "services";

export interface InboxItem {
  key: InboxKind;
  count: number;
  text: string;
  detail: string;
  to: string;
  tone: "warning" | "danger";
}

const plural = (n: number, one: string, many = `${one}s`) => `${formatCount(n)} ${n === 1 ? one : many}`;

/** The "Needs you" rows, most urgent first. Counts the caller can't see are
 * null in the response and never produce a row. */
export function inboxItems(counts: OverviewCounts): InboxItem[] {
  const items: InboxItem[] = [];
  if (counts.services && counts.services.issues > 0) {
    items.push({
      key: "services",
      count: counts.services.issues,
      text: `${plural(counts.services.issues, "service")} with issues`,
      detail: `${counts.services.total - counts.services.issues} of ${counts.services.total} healthy`,
      to: ROUTES.PLATFORM,
      tone: "danger",
    });
  }
  if (counts.payouts_pending && counts.payouts_pending.count > 0) {
    items.push({
      key: "payouts",
      count: counts.payouts_pending.count,
      text: `${plural(counts.payouts_pending.count, "payout")} to pay`,
      detail: counts.payouts_pending.totals.map((t) => formatMoney(t.amount, t.currency)).join(" + "),
      to: ROUTES.WITHDRAWALS,
      tone: "warning",
    });
  }
  if (counts.orders_attention && counts.orders_attention > 0) {
    items.push({
      key: "orders",
      count: counts.orders_attention,
      text: `${plural(counts.orders_attention, "order")} need${counts.orders_attention === 1 ? "s" : ""} attention`,
      detail: "Unconfirmed, not shipped, or stuck in delivery",
      to: `${ROUTES.SALES}?attention=1`,
      tone: "warning",
    });
  }
  if (counts.applications_pending && counts.applications_pending > 0) {
    items.push({
      key: "applications",
      count: counts.applications_pending,
      text: `${plural(counts.applications_pending, "seller application")} waiting`,
      detail: "Approve or follow up",
      to: `${ROUTES.USER_MANAGEMENT}?tab=applications`,
      tone: "warning",
    });
  }
  return items;
}

/** True when the caller can see at least one inbox source. */
export const hasInboxSources = (counts: OverviewCounts) =>
  [counts.services, counts.payouts_pending, counts.orders_attention, counts.applications_pending].some((v) => v !== null && v !== undefined);
